import React, { useState, useEffect, useRef } from 'react';
import { Card, Tabs, Alert, Table, Tag, Select, Button, Space, DatePicker, Row, Col, Statistic, Radio, message } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { 
  VideoCameraOutlined, 
  FormOutlined,
  UserDeleteOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
// 导入子页面
import VideoAttendance from './VideoAttendance';
import ManualAttendance from './ManualAttendance';
const { TabPane } = Tabs;
const { Option } = Select;
function parseSummary(summary) {
  if (!summary) return {};
  if (typeof summary === 'object') return summary;
  try {
    const parsed = JSON.parse(summary);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
}
function resolveSmartAttendanceMediaUrl(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const configured = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://127.0.0.1:4100');
  const base = configured.replace(/\/api\/v2\/?$/, '').replace(/\/api\/?$/, '').replace(/\/$/, '');
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
}
function getSmartAttendanceGatewayUrl() {
  const configured = process.env.REACT_APP_GATEWAY_URL || process.env.REACT_APP_WS_URL;
  if (configured) return configured;
  const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
  const url = new URL(baseUrl, window.location.origin);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  if ((url.hostname === '127.0.0.1' || url.hostname === 'localhost') && (url.port === '4100' || url.port === '3000')) {
    url.port = '4200';
  }
  url.pathname = '/ws';
  url.search = '';
  url.hash = '';
  return url.toString();
}
function normalizeSmartAttendanceResult(item = {}, reviewRequired = false) {
  let metadata = item.metadata;
  if (typeof metadata === 'string') {
    try {
      metadata = JSON.parse(metadata);
    } catch (_) {
      metadata = {};
    }
  }
  metadata = metadata && typeof metadata === 'object' ? metadata : {};
  const nestedMetadata = metadata.metadata && typeof metadata.metadata === 'object' ? metadata.metadata : {};
  metadata = { ...metadata, ...nestedMetadata };
  delete metadata.metadata;
  const status = String(item.status || 'unknown');
  const studentId = item.studentId || item.student_id || item.id;
  const studentName = item.studentName || item.student_name || `学生${studentId || ''}`;
  const seatLabel = item.seatLabel || item.seat_label || metadata.seatLabel || metadata.seat_label || '';
  const seatNumber = item.seatNumber || item.seat_number || metadata.seatNumber || metadata.seat_number || '';
  const cropImageUrl = resolveSmartAttendanceMediaUrl(
    item.cropImageUrl || item.crop_image_url || metadata.cropImageUrl || metadata.crop_image_url || ''
  );
  const confidence = item.confidence ?? metadata.confidence ?? metadata.score;
  return {
    ...item,
    studentId,
    studentName,
    seatLabel,
    seatNumber,
    seatDisplay: seatNumber ? `座号 ${seatNumber}` : (seatLabel || '-'),
    status,
    confidence,
    cropImageUrl,
    reviewRequired,
    reviewDirty: false
  };
}
function summarizeSmartAttendanceResults(results = []) {
  return results.reduce((summary, item) => {
    const key = ['present', 'absent', 'late', 'leave', 'unknown'].includes(item.status) ? item.status : 'unknown';
    summary[key] += 1;
    summary.total += 1;
    return summary;
  }, { total: 0, present: 0, absent: 0, late: 0, leave: 0, unknown: 0 });
}
const SMART_ATTENDANCE_STATUS = {
  present: { label: '到场', color: 'green' },
  absent: { label: '疑似缺勤', color: 'red' },
  late: { label: '迟到', color: 'orange' },
  leave: { label: '请假', color: 'blue' },
  unknown: { label: '待确认', color: 'default' }
};
function seatCoordinate(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : 0;
}
function normalizeAislePositions(value) {
  if (Array.isArray(value)) return value.map(seatCoordinate).filter(Boolean);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(seatCoordinate).filter(Boolean) : [];
    } catch (_) {
      return value.split(',').map(seatCoordinate).filter(Boolean);
    }
  }
  return [];
}
// eslint-disable-next-line no-unused-vars
const LocalAiAttendancePanel = () => {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [session, setSession] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });
  const loadClasses = async () => {
    try {
      const res = await axios.get('/api/v2/teacher-class-management/my-classes', { headers: authHeaders() });
      const payload = res.data?.data || res.data;
      const list = Array.isArray(payload) ? payload : (payload.classes || payload.classList || []);
      const normalized = list.map((item) => ({
        ...item,
        classId: String(item.classId || item.class_id || item.id || ''),
        className: item.className || item.class_name || item.name || `班级${item.classId || item.id || ''}`
      })).filter((item) => item.classId);
      setClasses(normalized);
      if (!classId && normalized[0]) setClassId(normalized[0].classId);
    } catch (error) {
      console.error('加载班级失败:', error);
    }
  };
  const refreshSession = async (targetSessionId = session?.sessionId) => {
    if (!targetSessionId) return;
    const res = await axios.get(`/api/v2/smart-attendance/sessions/${targetSessionId}`, { headers: authHeaders() });
    const data = res.data?.data || res.data;
    const summary = parseSummary(data.summary);
    setSession({
      ...data,
      summary,
      sessionId: String(data.sessionId || data.id || targetSessionId)
    });
    setResults(data.results || []);
  };
  const startSession = async () => {
    if (!classId) return;
    setLoading(true);
    try {
      const res = await axios.post('/api/v2/smart-attendance/sessions', {
        classId,
        mode: 'auto'
      }, { headers: authHeaders() });
      const data = res.data?.data || res.data;
      const nextSession = {
        ...data,
        sessionId: String(data.sessionId || data.id || '')
      };
      setSession(nextSession);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const summary = parseSummary(session?.summary);
  const annotatedUrl = resolveSmartAttendanceMediaUrl(summary.annotatedSnapshotUrl);
  const snapshotUrl = resolveSmartAttendanceMediaUrl(summary.snapshotUrl);
  const statusMap = {
    created: '已创建',
    starting: '启动中',
    running: '运行中',
    completed: summary.reviewRequired ? '已回传，待确认' : '已完成',
    stopped: '已停止',
    failed: '失败'
  };
  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select value={classId} style={{ width: 220 }} onChange={setClassId} placeholder="选择班级">
          {classes.map((item) => (
            <Option key={item.classId} value={item.classId}>{item.className}</Option>
          ))}
        </Select>
        <Button type="primary" icon={<VideoCameraOutlined />} loading={loading} onClick={startSession}>
          发起本地AI考勤
        </Button>
        <Button icon={<ReloadOutlined />} disabled={!session?.sessionId} onClick={() => refreshSession()}>
          刷新结果
        </Button>
      </Space>
      {session && (
        <Card size="small" title="本地AI考勤测试结果">
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}><Statistic title="状态" value={statusMap[session.status] || session.status || '-'} /></Col>
            <Col span={6}><Statistic title="AI识别人数" value={summary.aiPersonCount || 0} /></Col>
            <Col span={6}><Statistic title="座位推算人数" value={summary.aiSeatMatchedCount || 0} /></Col>
            <Col span={6}><Statistic title="结果条数" value={results.length} /></Col>
          </Row>
          {(annotatedUrl || snapshotUrl) && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 700 }}>{annotatedUrl ? 'AI检测图' : '教室原图'}</div>
              <img
                src={annotatedUrl || snapshotUrl}
                alt="AI检测图"
                style={{ width: '100%', maxHeight: 520, objectFit: 'contain', background: '#111827', borderRadius: 8 }}
              />
            </div>
          )}
          <Table
            rowKey={(row) => row.id || row.student_id}
            size="small"
            pagination={{ pageSize: 12 }}
            dataSource={results}
            columns={[
              { title: '学生', dataIndex: 'student_name', key: 'student_name' },
              { title: '座位', dataIndex: 'seat_label', key: 'seat_label', width: 120 },
              { title: '状态', dataIndex: 'status', key: 'status', width: 120, render: (value) => <Tag color={value === 'present' ? 'green' : 'default'}>{value}</Tag> },
              { title: '置信度', dataIndex: 'confidence', key: 'confidence', width: 120, render: (value) => value == null ? '-' : `${Math.round(Number(value) * 100)}%` }
            ]}
          />
        </Card>
      )}
    </div>
  );
};
/**
 * 全校缺勤名单子组件（供学校管理员使用）
 */
const LocalAiAttendanceAdminPanel = () => {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [classSearching, setClassSearching] = useState(false);
  const [mode, setMode] = useState('auto');
  const [session, setSession] = useState(null);
  const [results, setResults] = useState([]);
  const [seats, setSeats] = useState([]);
  const [seatLayout, setSeatLayout] = useState({ rows: 0, cols: 0, aislePositions: [], podiumPosition: 'top' });
  const [seatLoading, setSeatLoading] = useState(false);
  const [selectedSeatStudentId, setSelectedSeatStudentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const gatewaySocketRef = useRef(null);
  const gatewayReconnectTimerRef = useRef(null);
  const realtimeEnabledRef = useRef(true);
  const sessionIdRef = useRef('');
  const realtimeResultTimersRef = useRef([]);
  const classSearchTimerRef = useRef(null);
  const clearRealtimeResultTimers = () => {
    realtimeResultTimersRef.current.forEach((timer) => clearTimeout(timer));
    realtimeResultTimersRef.current = [];
  };
  const refreshSession = async (targetSessionId = session?.sessionId) => {
    if (!targetSessionId) return null;
    try {
      const response = await api.get(`/smart-attendance/sessions/${targetSessionId}`);
      const data = response.data?.data || response.data;
      const summary = parseSummary(data.summary);
      const reviewRequired = [true, 1, '1', 'true'].includes(summary.reviewRequired);
      const nextSession = {
        ...data,
        summary,
        sessionId: String(data.sessionId || data.id || targetSessionId)
      };
      setSession(nextSession);
      setResults((data.results || []).map((item) => normalizeSmartAttendanceResult(item, reviewRequired)));
      return nextSession;
    } catch (error) {
      message.error(error.response?.data?.message || '刷新考勤结果失败');
      return null;
    }
  };
  const mergeRealtimeResult = (rawItem, reviewRequired) => {
    const incoming = normalizeSmartAttendanceResult(rawItem, reviewRequired);
    if (!incoming.studentId) return;
    setResults((current) => {
      const index = current.findIndex((item) => String(item.studentId) === String(incoming.studentId));
      if (index < 0) return [...current, incoming];
      const next = [...current];
      next[index] = {
        ...next[index],
        ...incoming,
        cropImageUrl: incoming.cropImageUrl || next[index].cropImageUrl,
        reviewDirty: next[index].reviewDirty || false
      };
      return next;
    });
  };
  const applyRealtimeSessionUpdate = (data) => {
    if (!data?.sessionId || String(data.sessionId) !== String(sessionIdRef.current)) return;
    const summary = parseSummary(data.summary);
    const reviewRequired = [true, 1, '1', 'true'].includes(summary.reviewRequired);
    setSession((current) => current ? {
      ...current,
      status: data.status || current.status,
      summary: { ...parseSummary(current.summary), ...summary }
    } : current);
    clearRealtimeResultTimers();
    const incomingResults = Array.isArray(data.results) ? data.results : [];
    incomingResults.forEach((item, index) => {
      const timer = window.setTimeout(
        () => mergeRealtimeResult(item, reviewRequired),
        Math.min(index * 90, 4500)
      );
      realtimeResultTimersRef.current.push(timer);
    });
  };
  const connectRealtimeGateway = () => {
    if (!realtimeEnabledRef.current) return;
    if (gatewaySocketRef.current && [WebSocket.OPEN, WebSocket.CONNECTING].includes(gatewaySocketRef.current.readyState)) {
      return;
    }
    let socket;
    try {
      socket = new WebSocket(getSmartAttendanceGatewayUrl());
    } catch (_) {
      return;
    }
    gatewaySocketRef.current = socket;
    socket.onopen = () => {
      socket.send(JSON.stringify({
        type: 'identity',
        role: 'teacher',
        token: localStorage.getItem('token')
      }));
    };
    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'smart-attendance.session.updated') applyRealtimeSessionUpdate(data);
      } catch (_) {
        // Ignore non-JSON gateway frames.
      }
    };
    socket.onclose = () => {
      if (gatewaySocketRef.current === socket) gatewaySocketRef.current = null;
      if (realtimeEnabledRef.current && !gatewayReconnectTimerRef.current) {
        gatewayReconnectTimerRef.current = window.setTimeout(() => {
          gatewayReconnectTimerRef.current = null;
          connectRealtimeGateway();
        }, 3000);
      }
    };
  };
  const loadSeatLayout = async (targetClassId) => {
    if (!targetClassId) {
      setSeats([]);
      setSeatLayout({ rows: 0, cols: 0, aislePositions: [], podiumPosition: 'top' });
      return;
    }
    setSeatLoading(true);
    try {
      const response = await api.get(`/seats/${targetClassId}`);
      const payload = response.data?.data || response.data || {};
      const seatList = Array.isArray(payload.seats) ? payload.seats : [];
      const layout = payload.layout || {};
      const maxRow = seatList.reduce((value, item) => Math.max(value, seatCoordinate(item.row_number ?? item.rowNumber)), 0);
      const maxCol = seatList.reduce((value, item) => Math.max(value, seatCoordinate(item.col_number ?? item.colNumber)), 0);
      setSeats(seatList);
      setSeatLayout({
        rows: seatCoordinate(layout.rows) || maxRow,
        cols: seatCoordinate(layout.cols) || maxCol,
        aislePositions: normalizeAislePositions(layout.aislePositions ?? layout.aisle_positions),
        podiumPosition: layout.podiumPosition || layout.podium_position || 'top'
      });
    } catch (error) {
      setSeats([]);
      setSeatLayout({ rows: 0, cols: 0, aislePositions: [], podiumPosition: 'top' });
      message.error(error.response?.data?.message || '加载班级座位表失败');
    } finally {
      setSeatLoading(false);
    }
  };
  const handleClassChange = (value) => {
    const nextClassId = value || '';
    clearRealtimeResultTimers();
    sessionIdRef.current = '';
    setClassId(nextClassId);
    setSession(null);
    setResults([]);
    setSelectedSeatStudentId('');
    loadSeatLayout(nextClassId);
  };
  const loadClasses = async (keyword = '') => {
    setClassSearching(true);
    try {
      const trimmedKeyword = String(keyword || '').trim();
      const response = await api.get('/classes', {
        params: {
          page: 1,
          pageSize: 50,
          schoolId: user?.workbenchSchoolId,
          ...(trimmedKeyword ? { search: trimmedKeyword } : {})
        }
      });
      const payload = response.data?.data || response.data;
      const list = Array.isArray(payload) ? payload : (payload.classes || payload.classList || []);
      const normalized = list.map((item) => ({
        ...item,
        classId: String(item.classId || item.class_id || item.id || ''),
        className: item.className || item.class_name || item.name || `班级${item.classId || item.id || ''}`,
        schoolName: item.schoolName || item.school_name || ''
      })).filter((item) => item.classId);
      setClasses(normalized);
    } catch (error) {
      message.error(error.response?.data?.message || '加载班级失败');
    } finally {
      setClassSearching(false);
    }
  };
  useEffect(() => {
    realtimeEnabledRef.current = true;
    loadClasses();
    connectRealtimeGateway();
    return () => {
      realtimeEnabledRef.current = false;
      clearRealtimeResultTimers();
      if (gatewayReconnectTimerRef.current) {
        clearTimeout(gatewayReconnectTimerRef.current);
        gatewayReconnectTimerRef.current = null;
      }
      if (gatewaySocketRef.current) {
        gatewaySocketRef.current.close();
        gatewaySocketRef.current = null;
      }
      if (classSearchTimerRef.current) {
        clearTimeout(classSearchTimerRef.current);
        classSearchTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const searchClasses = (keyword) => {
    if (classSearchTimerRef.current) clearTimeout(classSearchTimerRef.current);
    classSearchTimerRef.current = setTimeout(() => loadClasses(keyword), 400);
  };
  const startSession = async () => {
    if (!classId) {
      message.warning('请先选择班级');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/smart-attendance/sessions', { classId, mode });
      const data = response.data?.data || response.data;
      const nextSession = {
        ...data,
        status: data.commandStatus === 'sent' ? 'starting' : 'created',
        sessionId: String(data.sessionId || data.id || ''),
        className: classes.find((item) => item.classId === classId)?.className || ''
      };
      setSession(nextSession);
      sessionIdRef.current = nextSession.sessionId;
      setResults([]);
      message.success(data.commandStatus === 'sent' ? '已向教室电脑发送考勤指令' : '会话已创建，但教室电脑可能不在线');
    } catch (error) {
      message.error(error.response?.data?.message || '发起智能考勤失败');
    } finally {
      setLoading(false);
    }
  };
  const stopSession = async () => {
    if (!session?.sessionId) return;
    setLoading(true);
    try {
      await api.post(`/smart-attendance/sessions/${session.sessionId}/stop`);
      setSession((current) => current ? { ...current, status: 'stopped' } : current);
      message.success('已发送停止指令');
    } catch (error) {
      message.error(error.response?.data?.message || '停止智能考勤失败');
    } finally {
      setLoading(false);
    }
  };
  const updateResultStatus = (studentId, status) => {
    setResults((current) => current.map((item) => (
      String(item.studentId) === String(studentId)
        ? { ...item, status, reviewDirty: true }
        : item
    )));
  };
  const confirmSession = async () => {
    if (!session?.sessionId || session.status !== 'completed') return;
    setConfirming(true);
    try {
      const reviewedResults = results
        .filter((item) => item.reviewDirty && item.studentId)
        .map((item) => ({ studentId: item.studentId, status: item.status }));
      if (reviewedResults.length) {
        await api.patch(`/smart-attendance/sessions/${session.sessionId}/results`, { results: reviewedResults });
      }
      const response = await api.post(`/smart-attendance/sessions/${session.sessionId}/confirm`, {
        teacherName: '系统超级管理员'
      });
      message.success(`已确认并写入 ${response.data?.data?.saved || 0} 条考勤`);
      await refreshSession(session.sessionId);
    } catch (error) {
      message.error(error.response?.data?.message || '确认考勤失败');
    } finally {
      setConfirming(false);
    }
  };
  const summary = parseSummary(session?.summary);
  const reviewRequired = [true, 1, '1', 'true'].includes(summary.reviewRequired);
  const annotatedUrl = resolveSmartAttendanceMediaUrl(summary.annotatedSnapshotUrl);
  const snapshotUrl = resolveSmartAttendanceMediaUrl(summary.snapshotUrl);
  const resultSummary = summarizeSmartAttendanceResults(results);
  const statusMap = {
    created: '已创建',
    starting: '启动中',
    running: '运行中',
    completed: reviewRequired ? '已回传，待确认' : '已完成',
    stopped: '已停止',
    failed: '失败'
  };
  const active = session && ['starting', 'running'].includes(session.status);
  const resultByStudentId = new Map(
    results
      .filter((item) => item.studentId)
      .map((item) => [String(item.studentId), item])
  );
  const seatByPosition = new Map(
    seats.map((item) => [
      `${seatCoordinate(item.row_number ?? item.rowNumber)}-${seatCoordinate(item.col_number ?? item.colNumber)}`,
      item
    ])
  );
  const occupiedSeats = seats
    .filter((item) => item.student_id || item.studentId)
    .sort((left, right) => (
      seatCoordinate(left.row_number ?? left.rowNumber) - seatCoordinate(right.row_number ?? right.rowNumber)
      || seatCoordinate(left.col_number ?? left.colNumber) - seatCoordinate(right.col_number ?? right.colNumber)
    ));
  const unseatedStudents = seats.filter((item) => (
    (item.student_id || item.studentId)
    && (!seatCoordinate(item.row_number ?? item.rowNumber) || !seatCoordinate(item.col_number ?? item.colNumber))
  ));
  const cropResults = results
    .filter((item) => item.cropImageUrl)
    .sort((left, right) => {
      const leftSeat = occupiedSeats.find((seat) => String(seat.student_id || seat.studentId) === String(left.studentId));
      const rightSeat = occupiedSeats.find((seat) => String(seat.student_id || seat.studentId) === String(right.studentId));
      return seatCoordinate(leftSeat?.row_number ?? leftSeat?.rowNumber) - seatCoordinate(rightSeat?.row_number ?? rightSeat?.rowNumber)
        || seatCoordinate(leftSeat?.col_number ?? leftSeat?.colNumber) - seatCoordinate(rightSeat?.col_number ?? rightSeat?.colNumber);
    });
  const seatAppearance = (result, hasStudent) => {
    if (!hasStudent) {
      return { background: '#f8fafc', border: '#cbd5e1', color: '#94a3b8', label: '空' };
    }
    if (!result) {
      return active
        ? { background: '#fffbeb', border: '#fbbf24', color: '#92400e', label: '检测中' }
        : { background: '#ffffff', border: '#dbeafe', color: '#334155', label: '待启动' };
    }
    const appearances = {
      present: { background: '#dcfce7', border: '#22c55e', color: '#166534', label: '到场' },
      absent: { background: '#fee2e2', border: '#ef4444', color: '#991b1b', label: '疑似缺勤' },
      late: { background: '#ffedd5', border: '#f97316', color: '#9a3412', label: '迟到' },
      leave: { background: '#dbeafe', border: '#3b82f6', color: '#1e40af', label: '请假' },
      unknown: { background: '#fef3c7', border: '#f59e0b', color: '#92400e', label: active ? '检测中' : '待确认' }
    };
    return appearances[result.status] || appearances.unknown;
  };
  const focusStudentCrop = (studentId) => {
    if (!studentId) return;
    const key = String(studentId);
    setSelectedSeatStudentId(key);
    window.setTimeout(() => {
      document.getElementById(`attendance-crop-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  };
  const renderAisle = (key) => (
    <div
      key={key}
      style={{
        width: 20,
        flex: '0 0 20px',
        minHeight: 42,
        borderLeft: '2px dashed #94a3b8',
        borderRight: '2px dashed #94a3b8',
        background: '#f8fafc'
      }}
    />
  );
  const renderSeatBoard = () => {
    if (!classId) return null;
    if (!seatLayout.rows || !seatLayout.cols) {
      return (
        <Card size="small" loading={seatLoading} title="班级座位表" style={{ marginBottom: 16 }}>
          <Alert type="info" showIcon message="该班级尚未设置座位表" />
        </Card>
      );
    }
    const podiumAtBottom = String(seatLayout.podiumPosition).toLowerCase() === 'bottom';
    const podium = (
      <div style={{ textAlign: 'center', margin: podiumAtBottom ? '16px 0 0' : '0 0 16px' }}>
        <Tag color="blue">讲台方向</Tag>
      </div>
    );
    const boardMinWidth = 64 + seatLayout.cols * 92 + seatLayout.aislePositions.length * 20;
    return (
      <Card
        size="small"
        loading={seatLoading}
        title={`班级座位表${active ? ' · 正在自动检测' : ''}`}
        style={{ marginBottom: 16 }}
      >
        {!podiumAtBottom && podium}
        <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
          <div style={{ minWidth: boardMinWidth, padding: 12, borderRadius: 14, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', marginBottom: 8 }}>
              <div style={{ width: 56, flex: '0 0 56px' }} />
              {Array.from({ length: seatLayout.cols }, (_, columnIndex) => {
                const col = columnIndex + 1;
                return (
                  <React.Fragment key={`column-${col}`}>
                    <div style={{ width: 84, flex: '0 0 84px', textAlign: 'center', padding: '7px 4px', borderRadius: 8, background: '#eff6ff', color: '#2563eb', fontWeight: 600 }}>
                      第{col}列
                    </div>
                    {seatLayout.aislePositions.includes(col) && col < seatLayout.cols ? renderAisle(`header-aisle-${col}`) : null}
                  </React.Fragment>
                );
              })}
            </div>
            {Array.from({ length: seatLayout.rows }, (_, rowIndex) => {
              const row = rowIndex + 1;
              return (
                <div key={`row-${row}`} style={{ display: 'flex', gap: 8, alignItems: 'stretch', marginBottom: 8 }}>
                  <div style={{ width: 56, flex: '0 0 56px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, background: '#eff6ff', color: '#2563eb', fontWeight: 600 }}>
                    第{row}行
                  </div>
                  {Array.from({ length: seatLayout.cols }, (_, columnIndex) => {
                    const col = columnIndex + 1;
                    const seat = seatByPosition.get(`${row}-${col}`);
                    const studentId = seat?.student_id || seat?.studentId;
                    const studentName = seat?.student_name || seat?.studentName || '';
                    const hasStudent = Boolean(studentId && studentName);
                    const result = hasStudent ? resultByStudentId.get(String(studentId)) : null;
                    const appearance = seatAppearance(result, hasStudent);
                    const selected = hasStudent && String(studentId) === selectedSeatStudentId;
                    return (
                      <React.Fragment key={`seat-${row}-${col}`}>
                        <button
                          type="button"
                          onClick={() => hasStudent && focusStudentCrop(studentId)}
                          style={{
                            width: 84,
                            minHeight: 68,
                            flex: '0 0 84px',
                            padding: '7px 5px',
                            borderRadius: 10,
                            border: `${selected ? 3 : 2}px ${hasStudent ? 'solid' : 'dashed'} ${selected ? '#2563eb' : appearance.border}`,
                            background: appearance.background,
                            color: appearance.color,
                            cursor: hasStudent ? 'pointer' : 'default',
                            boxShadow: result?.status === 'present' ? '0 5px 16px rgba(34,197,94,0.20)' : 'none',
                            transition: 'all 0.25s ease'
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2, wordBreak: 'break-all' }}>
                            {hasStudent ? studentName : '空'}
                          </div>
                          <div style={{ marginTop: 5, fontSize: 11, opacity: 0.9 }}>{appearance.label}</div>
                        </button>
                        {seatLayout.aislePositions.includes(col) && col < seatLayout.cols ? renderAisle(`aisle-${row}-${col}`) : null}
                      </React.Fragment>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
        {podiumAtBottom && podium}
        {unseatedStudents.length > 0 && (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 10, border: '1px dashed #cbd5e1', background: '#fff' }}>
            <div style={{ marginBottom: 8, color: '#64748b' }}>未安排座位</div>
            <Space wrap>
              {unseatedStudents.map((seat) => <Tag key={seat.student_id || seat.studentId}>{seat.student_name || seat.studentName}</Tag>)}
            </Space>
          </div>
        )}
        <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <strong>学生现场切片</strong>
            <span style={{ color: '#64748b' }}>已生成 {cropResults.length} 张</span>
          </div>
          {cropResults.length ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
              {cropResults.map((item) => {
                const selected = String(item.studentId) === selectedSeatStudentId;
                return (
                  <button
                    type="button"
                    id={`attendance-crop-${item.studentId}`}
                    key={item.studentId}
                    onClick={() => setSelectedSeatStudentId(String(item.studentId))}
                    style={{
                      padding: 0,
                      overflow: 'hidden',
                      textAlign: 'left',
                      borderRadius: 12,
                      border: selected ? '3px solid #2563eb' : '1px solid #dbeafe',
                      background: '#fff',
                      cursor: 'pointer',
                      boxShadow: '0 6px 16px rgba(15,23,42,0.08)'
                    }}
                  >
                    <img src={item.cropImageUrl} alt={`${item.studentName}现场切片`} style={{ width: '100%', height: 126, objectFit: 'cover', display: 'block', background: '#f1f5f9' }} />
                    <div style={{ padding: '9px 10px' }}>
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>{item.studentName}</div>
                      <div style={{ marginTop: 3, color: '#64748b', fontSize: 12 }}>{item.seatDisplay}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: '24px 12px', textAlign: 'center', color: '#94a3b8', borderRadius: 10, background: '#f8fafc' }}>
              检测到学生并完成切片后，将在这里按座位顺序显示。
            </div>
          )}
        </div>
      </Card>
    );
  };
  return (
    <div>
      <Alert
        type="warning"
        showIcon
        message="系统超级管理员专用"
        description="此入口会向指定教室电脑发送智能考勤指令，仅用于系统联调和现场测试。"
        style={{ marginBottom: 16 }}
      />
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          value={classId || undefined}
          style={{ width: 360 }}
          onChange={handleClassChange}
          onSearch={searchClasses}
          onClear={() => {
            handleClassChange('');
            loadClasses();
          }}
          placeholder="输入学校或班级名称搜索"
          showSearch
          allowClear
          filterOption={false}
          loading={classSearching}
          notFoundContent={classSearching ? '搜索中...' : '未找到匹配班级'}
          disabled={Boolean(active)}
        >
          {classes.map((item) => (
            <Option key={item.classId} value={item.classId}>
              {item.schoolName ? `${item.schoolName} · ` : ''}{item.className}
            </Option>
          ))}
        </Select>
        <Radio.Group value={mode} onChange={(event) => setMode(event.target.value)} disabled={Boolean(active)}>
          <Radio.Button value="auto">自适应多帧考勤</Radio.Button>
          <Radio.Button value="single">单张照片考勤</Radio.Button>
          <Radio.Button value="manual">打开桌面窗口</Radio.Button>
        </Radio.Group>
        <Button type="primary" icon={<VideoCameraOutlined />} loading={loading} disabled={Boolean(active)} onClick={startSession}>
          发起智能考勤
        </Button>
        <Button danger loading={loading} disabled={!active} onClick={stopSession}>
          停止考勤
        </Button>
        <Button icon={<ReloadOutlined />} disabled={!session?.sessionId} onClick={() => refreshSession()}>
          刷新结果
        </Button>
      </Space>
      <div style={{ margin: '-8px 0 16px', color: '#64748b' }}>
        {mode === 'single'
          ? '单张模式只检测预热后的一张照片，适合与多帧结果进行对照。'
          : mode === 'auto'
            ? '多帧模式按画面稳定度使用 3、5 或 7 帧，以各帧人数中位数和对应代表帧作为结果。'
            : '桌面窗口模式用于人工查看摄像头画面并操作考勤。'}
      </div>
      {renderSeatBoard()}
      {session && (
        <Card size="small" title={`智能考勤结果${session.className ? ` · ${session.className}` : ''}`}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={4}><Statistic title="状态" value={statusMap[session.status] || session.status || '-'} /></Col>
            <Col span={4}><Statistic title="检测人数" value={summary.aiPersonCount || 0} /></Col>
            <Col span={4}><Statistic title="座位匹配" value={summary.aiSeatMatchedCount || 0} /></Col>
            <Col span={4}><Statistic title="到场" value={resultSummary.present} valueStyle={{ color: '#16a34a' }} /></Col>
            <Col span={4}><Statistic title="疑似缺勤" value={resultSummary.absent} valueStyle={{ color: '#dc2626' }} /></Col>
            <Col span={4}><Statistic title="待确认" value={resultSummary.unknown} valueStyle={{ color: '#d97706' }} /></Col>
          </Row>
          {(annotatedUrl || snapshotUrl) && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 700 }}>{annotatedUrl ? 'AI检测图' : '教室原图'}</div>
              <img src={annotatedUrl || snapshotUrl} alt="AI检测图" style={{ width: '100%', maxHeight: 520, objectFit: 'contain', background: '#111827', borderRadius: 8 }} />
            </div>
          )}
          {session.status === 'completed' && reviewRequired && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="本次结果需要确认"
              description="可以在下方修改学生状态，确认后才会写入正式考勤记录。"
              action={<Button type="primary" size="small" loading={confirming} onClick={confirmSession}>确认并写入考勤</Button>}
            />
          )}
          <Table
            rowKey={(row, index) => row.studentId || row.id || row.student_id || index}
            size="small"
            pagination={{ pageSize: 12 }}
            dataSource={results}
            columns={[
              { title: '学生', dataIndex: 'studentName', key: 'studentName', width: 150 },
              { title: '座位', dataIndex: 'seatDisplay', key: 'seatDisplay', width: 120 },
              {
                title: '状态',
                dataIndex: 'status',
                key: 'status',
                width: 150,
                render: (value, row) => (
                  <Select size="small" value={value} style={{ width: 120 }} onChange={(nextStatus) => updateResultStatus(row.studentId, nextStatus)}>
                    {Object.entries(SMART_ATTENDANCE_STATUS).map(([key, option]) => (
                      <Option key={key} value={key}>{option.label}</Option>
                    ))}
                  </Select>
                )
              },
              {
                title: 'AI置信度',
                dataIndex: 'confidence',
                key: 'confidence',
                width: 120,
                render: (value) => value == null || Number.isNaN(Number(value)) ? '-' : `${Math.round(Number(value) * 100)}%`
              },
              {
                title: '检测切片',
                dataIndex: 'cropImageUrl',
                key: 'cropImageUrl',
                width: 92,
                render: (value) => value ? <img src={value} alt="学生检测切片" style={{ width: 56, height: 42, objectFit: 'cover', borderRadius: 4 }} /> : '-'
              },
              {
                title: '标记',
                key: 'review',
                width: 80,
                render: (_, row) => row.reviewDirty ? <Tag color="gold">已修改</Tag> : null
              }
            ]}
          />
        </Card>
      )}
    </div>
  );
};
const SchoolAbsentList = () => {
  const [loading, setLoading] = useState(false);
  const [absentList, setAbsentList] = useState([]);
  const [statusFilter, setStatusFilter] = useState('absent');
  const [dateFilter, setDateFilter] = useState(moment().format('YYYY-MM-DD'));
  const fetchAbsentStudents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {
        status: statusFilter,
        startDate: dateFilter,
        endDate: dateFilter,
        pageSize: 200
      };
      const res = await axios.get('/api/v2/attendance/records', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setAbsentList(res.data.data || []);
      }
    } catch (err) {
      console.error('获取缺勤名单失败:', err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchAbsentStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, dateFilter]);
  const columns = [
    {
      title: '学生姓名',
      dataIndex: 'student_name',
      key: 'student_name',
      width: 120
    },
    {
      title: '班级',
      dataIndex: 'class_name',
      key: 'class_name',
      width: 150
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s) => {
        const map = { absent: { color: 'red', text: '缺勤' }, late: { color: 'orange', text: '迟到' }, leave: { color: 'blue', text: '请假' } };
        const cfg = map[s] || { color: 'default', text: s };
        return <Tag color={cfg.color}>{cfg.text}</Tag>;
      }
    },
    {
      title: '考勤时段',
      dataIndex: 'session_type',
      key: 'session_type',
      width: 120,
      render: (s) => {
        const map = { morning: '早读', forenoon: '上午', afternoon: '下午', evening: '晚自习' };
        return map[s] || s || '-';
      }
    },
    {
      title: '登记时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (t) => t ? moment(t).format('HH:mm') : '-'
    },
    {
      title: '备注',
      dataIndex: 'remarks',
      key: 'remarks',
      ellipsis: true
    }
  ];
  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: 120 }}
        >
          <Option value="absent">缺勤</Option>
          <Option value="late">迟到</Option>
          <Option value="leave">请假</Option>
        </Select>
        <DatePicker
          value={moment(dateFilter)}
          onChange={(d) => setDateFilter(d ? d.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'))}
          allowClear={false}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchAbsentStudents}
          loading={loading}
        >
          刷新
        </Button>
        <span style={{ color: '#888' }}>共 {absentList.length} 位学生</span>
      </Space>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={absentList}
        loading={loading}
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
    </div>
  );
};
/**
 * 智能考勤主页面
 * 包含：视频考勤、手动考勤、全校缺勤名单三个Tab
 */
const SmartAttendance = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isPlatformAdmin = Boolean(user && (
    user.username === 'admin' ||
    user.role === 'super_admin' ||
    user.role === 'platform_admin' ||
    user.role === 'admin'
  ));
  const initialTab = searchParams.get('tab') === 'manual'
    ? 'manual'
    : searchParams.get('tab') === 'absent'
      ? 'absent'
      : searchParams.get('tab') === 'local-ai' && isPlatformAdmin
        ? 'local-ai'
        : 'video';
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'local-ai' && !isPlatformAdmin) {
      setActiveTab('video');
      return;
    }
    if (tab === 'manual' || tab === 'video' || tab === 'absent' || (tab === 'local-ai' && isPlatformAdmin)) {
      setActiveTab(tab);
    }
  }, [searchParams, isPlatformAdmin]);
  // 判断是否为学校管理员（根据 localStorage 中的用户信息）
  const isSchoolAdmin = user?.role === 'school_admin' || user?.userType === 'school_admin';
  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Alert
          message="智能考勤系统"
          description="支持视频考勤（按人数/AI人脸识别）和手动考勤两种方式"
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          size="large"
        >
          <TabPane
            tab={
              <span>
                <VideoCameraOutlined />
                视频考勤
              </span>
            }
            key="video"
          >
            <VideoAttendance />
          </TabPane>
          {isPlatformAdmin && (
            <TabPane
              tab={
                <span>
                  <VideoCameraOutlined />
                  智能考勤触发
                </span>
              }
              key="local-ai"
            >
              <LocalAiAttendanceAdminPanel />
            </TabPane>
          )}
          <TabPane
            tab={
              <span>
                <FormOutlined />
                手动考勤
              </span>
            }
            key="manual"
          >
            <ManualAttendance />
          </TabPane>
          {isSchoolAdmin && (
            <TabPane
              tab={
                <span>
                  <UserDeleteOutlined />
                  全校缺勤名单
                </span>
              }
              key="absent"
            >
              <SchoolAbsentList />
            </TabPane>
          )}
        </Tabs>
      </Card>
    </div>
  );
};
export default SmartAttendance;
